import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight, Phone, Mail } from "lucide-react";
import Logo from "../Logo";
import DemoSwitch from "./DemoSwitch";

const LINKS = [
  { label: "Home", href: "#top-v3" },
  { label: "Projects", href: "#projects" },
  { label: "Philosophy", href: "#philosophy" },
  { label: "Live Progress", href: "#progress" },
  { label: "Contact", href: "#contact" },
];

export default function NavV3() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="nav-v3"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-editorial/90 backdrop-blur-xl border-b border-borderlt"
            : "bg-editorial/70 backdrop-blur-md border-b border-borderlt/60"
        }`}
      >
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 h-[110px] flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-v3-menu"
              aria-label="Open menu"
              className="group flex items-center gap-3 text-obsidian hover:text-emerald_kl transition-colors duration-500"
            >
              <span className="relative flex flex-col gap-1.5 w-6">
                <span className="h-px w-full bg-current" />
                <span className="h-px w-4 bg-current group-hover:w-full transition-all duration-300" />
              </span>
              <span className="hidden md:inline chapter-num tracking-[0.28em]">MENU</span>
            </button>
          </div>

          <Link to="/v3" data-testid="nav-v3-logo" className="absolute left-1/2 -translate-x-1/2 z-10">
            <Logo variant="dark" size={140} />
          </Link>

          <div className="flex items-center gap-3 flex-1 justify-end">
            <div className="hidden lg:block mr-2">
              <DemoSwitch active="v3" scrolled={true} />
            </div>
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-v3-search"
              className="group flex items-center gap-2 text-obsidian hover:text-emerald_kl transition-colors duration-500"
              aria-label="Search"
            >
              <span className="hidden md:inline chapter-num tracking-[0.28em]">SEARCH</span>
              <Search className="w-4 h-4" />
            </button>
            <a
              href="#contact"
              data-testid="nav-v3-cta"
              className="hidden md:inline-flex items-center gap-2 h-10 px-5 text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-500 bg-obsidian text-editorial hover:bg-emerald_kl"
            >
              Enquire <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              data-testid="nav-v3-drawer"
              className="fixed inset-y-0 left-0 z-50 w-full sm:w-[440px] md:w-[520px] bg-editorial text-obsidian flex flex-col"
            >
              <div className="h-[110px] px-8 flex items-center justify-between border-b border-obsidian/10">
                <Logo variant="dark" size={100} />
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-10 h-10 grid place-items-center text-obsidian hover:bg-obsidian hover:text-editorial transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-8 py-10">
                <div className="chapter-num text-obsidian/50 mb-6">NAVIGATE</div>
                <ul className="space-y-1">
                  {LINKS.map((l, i) => (
                    <motion.li
                      key={l.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.06, duration: 0.5 }}
                      className="border-b border-obsidian/10"
                    >
                      <a
                        href={l.href}
                        onClick={() => setMenuOpen(false)}
                        className="group flex items-center justify-between py-5 hover:pl-2 transition-all duration-300"
                      >
                        <span className="font-serif-display text-4xl md:text-5xl group-hover:italic transition-all">
                          {l.label}
                        </span>
                        <ArrowUpRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-emerald_kl" />
                      </a>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-12">
                  <div className="chapter-num text-obsidian/50 mb-4">STUDIO</div>
                  <p className="text-obsidian/80 text-sm leading-relaxed max-w-sm">
                    MGM Heights, Plot 140/9957,<br />
                    K living · 3rd Floor, Kalinga Nagar,<br />
                    Bhubaneswar 751003, Odisha.
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  <a href="tel:08908101907" className="flex items-center gap-3 text-obsidian hover:text-emerald_kl transition-colors">
                    <Phone className="w-4 h-4 text-emerald_kl" />
                    <span className="text-base">089081 01907</span>
                  </a>
                  <a href="mailto:studio@kliving.in" className="flex items-center gap-3 text-obsidian hover:text-emerald_kl transition-colors">
                    <Mail className="w-4 h-4 text-emerald_kl" />
                    <span className="text-base">studio@kliving.in</span>
                  </a>
                </div>

                <div className="mt-10 border-t border-obsidian/10 pt-6">
                  <div className="chapter-num text-obsidian/50 mb-3">DEMO VERSIONS</div>
                  <DemoSwitch active="v3" scrolled={true} />
                </div>
              </nav>

              <div className="border-t border-obsidian/10 px-8 py-5 flex items-center justify-between chapter-num text-obsidian/50">
                <span>© KLIVING · MMXXV</span>
                <Link to="/admin/login" className="hover:text-emerald_kl">ADMIN →</Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-editorial"
            onClick={() => setSearchOpen(false)}
          >
            <motion.div
              initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-full flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-obsidian/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[110px] flex items-center justify-between">
                  <div className="chapter-num text-obsidian/60">SEARCH KLIVING</div>
                  <button onClick={() => setSearchOpen(false)} className="w-10 h-10 grid place-items-center text-obsidian hover:bg-obsidian hover:text-editorial transition-colors" aria-label="Close search">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-16 md:pt-24">
                  <div className="flex items-center gap-4 border-b border-obsidian/25 focus-within:border-emerald_kl transition-colors">
                    <Search className="w-6 h-6 md:w-7 md:h-7 text-obsidian/60" />
                    <input
                      autoFocus
                      placeholder="Search projects, locations, typologies…"
                      className="flex-1 bg-transparent border-0 py-5 md:py-6 text-obsidian text-2xl md:text-4xl font-serif-display placeholder:text-obsidian/30 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
